/**
 * Avatar Admin View
 *
 * Configure LiveKit + SadTalker avatar video calls, view service status,
 * manage active sessions, and review session history.
 */

import { html, nothing } from "lit";

export type AvatarServiceStatus = {
  livekit: {
    configured: boolean;
    url: string;
    apiKeySet: boolean;
  };
  sadtalker: {
    healthy: boolean;
    modelLoaded: boolean;
    gpuAvailable: boolean;
    version?: string;
    url: string;
  };
  activeSessions: number;
};

export type AvatarConfig = {
  livekitUrl: string;
  livekitApiKey?: string;
  livekitApiKeySet: boolean;
  livekitApiSecret?: string;
  livekitApiSecretSet: boolean;
  sadtalkerUrl: string;
  avatarImagePath: string;
  defaultMaxDuration: number;
  defaultTtsVoice: string;
  llmModel: string;
  workspaceDir: string;
};

export type AvatarSessionEntry = {
  id: string;
  projectId: string | null;
  projectName?: string | null;
  userId: string | null;
  userName?: string | null;
  taskId: string | null;
  livekitRoomId: string;
  livekitRoomUrl: string;
  status: string;
  avatarImageUrl: string | null;
  transcript: string | null;
  durationSeconds: number | null;
  metadata: Record<string, unknown>;
  createdAt: string | Date;
  completedAt: string | Date | null;
};

export type AvatarModelEntry = {
  id: string;
  name: string;
  provider: string;
};

export type AvatarAdminProps = {
  loading: boolean;
  error: string | null;
  status: AvatarServiceStatus | null;
  config: AvatarConfig | null;
  sessions: AvatarSessionEntry[];
  configEditMode: boolean;
  configForm: Partial<AvatarConfig> | null;
  selectedSession: AvatarSessionEntry | null;
  statusFilter: string;
  modelCatalog: AvatarModelEntry[];
  onRefresh: () => void;
  onRefreshStatus: () => void;
  onSelectSession: (id: string) => void;
  onEndSession: (id: string) => void;
  onEditConfig: () => void;
  onSaveConfig: () => void;
  onCancelEdit: () => void;
  onConfigFormChange: (field: string, value: string) => void;
  onStatusFilterChange: (status: string) => void;
};

/**
 * Build `<option>` elements for the avatar LLM model picker.
 * Mirrors the agent model picker pattern: shows all catalog models,
 * filters out embedding/TTS models, and preserves the current selection
 * even if it's not in the catalog.
 */
function buildAvatarModelOptions(current: string, catalog: AvatarModelEntry[]) {
  const options: Array<{ value: string; label: string }> = [];
  const seen = new Set<string>();

  for (const entry of catalog) {
    const key = `${entry.provider}/${entry.id}`;
    if (seen.has(key)) {
      continue;
    }
    // Skip embedding models
    if (entry.id.includes("embedding") || entry.id.includes("embed-text")) {
      continue;
    }
    // Skip TTS models
    if (entry.id.includes("-tts-")) {
      continue;
    }
    seen.add(key);
    options.push({ value: key, label: key });
  }

  // Preserve current selection even if not in catalog
  if (current && !seen.has(current)) {
    options.unshift({ value: current, label: `${current} (current)` });
  }

  return options.map(
    (o) => html`<option value=${o.value} ?selected=${current === o.value}>${o.label}</option>`,
  );
}

export function renderAvatarAdmin(props: AvatarAdminProps) {
  return html`
    <section class="avatar-layout" style="display:flex;flex-direction:column;gap:16px;">
      <!-- Service Status -->
      ${renderStatusCards(props)}

      <!-- Config + Sessions side by side -->
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;">
        <!-- Configuration -->
        <div class="card">
          ${
            props.configEditMode && props.configForm
              ? renderConfigForm(props)
              : renderConfigView(props)
          }
        </div>

        <!-- Sessions -->
        <div class="card">
          ${renderSessionsList(props)}
        </div>
      </div>

      <!-- Session Detail -->
      ${
        props.selectedSession
          ? html`<div class="card">${renderSessionDetail(props)}</div>`
          : nothing
      }
    </section>
  `;
}

function renderStatusCards(props: AvatarAdminProps) {
  const s = props.status;
  return html`
    <div class="card">
      <div style="display:flex;justify-content:space-between;align-items:center;">
        <div class="card-title">Avatar System Status</div>
        <button class="btn btn--sm" @click=${props.onRefreshStatus} ?disabled=${props.loading}>
          ${props.loading ? "Checking..." : "Check Status"}
        </button>
      </div>

      ${props.error ? html`<div class="callout danger" style="margin-top:8px;">${props.error}</div>` : nothing}

      ${
        s
          ? html`
            <div class="detail-stat-grid" style="margin-top:12px;">
              <div class="stat">
                <div class="stat-value">
                  <span class="pill pill--sm ${s.livekit.configured ? "pill--ok" : ""}">
                    ${s.livekit.configured ? "Connected" : "Not Configured"}
                  </span>
                </div>
                <div class="stat-label">LiveKit</div>
              </div>
              <div class="stat">
                <div class="stat-value">
                  <span class="pill pill--sm ${s.sadtalker.healthy ? "pill--ok" : ""}">
                    ${s.sadtalker.healthy ? "Healthy" : "Offline"}
                  </span>
                </div>
                <div class="stat-label">SadTalker</div>
              </div>
              <div class="stat">
                <div class="stat-value">
                  <span class="pill pill--sm ${s.sadtalker.gpuAvailable ? "pill--ok" : ""}">
                    ${s.sadtalker.gpuAvailable ? "Available" : "N/A"}
                  </span>
                </div>
                <div class="stat-label">GPU</div>
              </div>
              <div class="stat">
                <div class="stat-value">
                  <span class="pill pill--sm ${s.sadtalker.modelLoaded ? "pill--ok" : ""}">
                    ${s.sadtalker.modelLoaded ? "Loaded" : "Not Loaded"}
                  </span>
                </div>
                <div class="stat-label">Model</div>
              </div>
              <div class="stat">
                <div class="stat-value">${s.activeSessions}</div>
                <div class="stat-label">Active Sessions</div>
              </div>
              <div class="stat">
                <div class="stat-value">${s.sadtalker.version ?? "N/A"}</div>
                <div class="stat-label">SadTalker Version</div>
              </div>
            </div>

            <div class="muted" style="font-size:0.8em;margin-top:8px;">
              LiveKit: ${s.livekit.url}
              &middot; SadTalker: ${s.sadtalker.url}
            </div>
          `
          : html`
              <div class="muted" style="margin-top: 12px">
                Click "Check Status" to probe LiveKit and SadTalker services.
              </div>
            `
      }
    </div>
  `;
}

function renderConfigView(props: AvatarAdminProps) {
  const c = props.config;
  const fieldStyle =
    "display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px solid var(--border);";

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <div class="card-title">Configuration</div>
      <button class="btn btn--sm" @click=${props.onEditConfig}>Edit</button>
    </div>

    ${
      c
        ? html`
          <div style="margin-top:12px;">
            <div style=${fieldStyle}>
              <span>LiveKit URL</span>
              <span class="muted">${c.livekitUrl}</span>
            </div>
            <div style=${fieldStyle}>
              <span>LiveKit API Key</span>
              <span class="pill pill--sm ${c.livekitApiKeySet ? "pill--ok" : ""}">
                ${c.livekitApiKeySet ? "Set" : "Not Set"}
              </span>
            </div>
            <div style=${fieldStyle}>
              <span>LiveKit API Secret</span>
              <span class="pill pill--sm ${c.livekitApiSecretSet ? "pill--ok" : ""}">
                ${c.livekitApiSecretSet ? "Set" : "Not Set"}
              </span>
            </div>
            <div style=${fieldStyle}>
              <span>SadTalker URL</span>
              <span class="muted">${c.sadtalkerUrl}</span>
            </div>
            <div style=${fieldStyle}>
              <span>Avatar Image</span>
              <span class="muted">${c.avatarImagePath || "Not set"}</span>
            </div>
            <div style=${fieldStyle}>
              <span>Default Max Duration</span>
              <span class="muted">${c.defaultMaxDuration}s</span>
            </div>
            <div style=${fieldStyle}>
              <span>TTS Voice ID</span>
              <span class="muted">${c.defaultTtsVoice || "Not set"}</span>
            </div>
            <div style=${fieldStyle}>
              <span>LLM Model</span>
              <span class="muted">${c.llmModel || "Not set (uses agent default)"}</span>
            </div>
            <div style=${fieldStyle}>
              <span>Agent Workspace</span>
              <span class="muted">${c.workspaceDir || "~/i-gloobot-files"}</span>
            </div>
          </div>

          <div class="muted" style="font-size:0.8em;margin-top:12px;">
            Settings can be edited from this panel and persist for the gateway lifetime.
            API keys can also be set via environment variables (LIVEKIT_API_KEY, LIVEKIT_API_SECRET).
          </div>
        `
        : html`
            <div class="muted" style="margin-top: 12px">Loading configuration...</div>
          `
    }
  `;
}

function renderConfigForm(props: AvatarAdminProps) {
  const form = props.configForm!;
  const fieldStyle =
    "width:100%;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);margin-top:4px;";

  return html`
    <div class="card-title">Edit Configuration</div>

    <div style="display:flex;flex-direction:column;gap:12px;margin-top:12px;">
      <label>
        <strong>LiveKit URL</strong>
        <input
          type="text"
          .value=${form.livekitUrl ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("livekitUrl", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="ws://localhost:7880"
        />
      </label>

      <label>
        <strong>LiveKit API Key</strong>
        <input
          type="text"
          .value=${form.livekitApiKey ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("livekitApiKey", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="APIxxxxxxx"
        />
        <span class="muted" style="font-size:0.8em;">Leave blank to keep existing value</span>
      </label>

      <label>
        <strong>LiveKit API Secret</strong>
        <input
          type="password"
          .value=${form.livekitApiSecret ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("livekitApiSecret", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="Enter secret (hidden)"
        />
        <span class="muted" style="font-size:0.8em;">Leave blank to keep existing value</span>
      </label>

      <label>
        <strong>SadTalker URL</strong>
        <input
          type="text"
          .value=${form.sadtalkerUrl ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("sadtalkerUrl", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="http://localhost:8765"
        />
      </label>

      <label>
        <strong>Avatar Image Path</strong>
        <input
          type="text"
          .value=${form.avatarImagePath ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("avatarImagePath", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="/path/to/avatar-portrait.png"
        />
      </label>

      <label>
        <strong>TTS Voice ID</strong>
        <input
          type="text"
          .value=${form.defaultTtsVoice ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("defaultTtsVoice", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="ElevenLabs voice ID"
        />
      </label>

      <label>
        <strong>LLM Model</strong>
        <select
          style=${fieldStyle}
          @change=${(e: Event) => props.onConfigFormChange("llmModel", (e.target as HTMLSelectElement).value)}
        >
          <option value="" ?selected=${!form.llmModel}>-- Use agent default --</option>
          ${buildAvatarModelOptions(form.llmModel ?? "", props.modelCatalog)}
        </select>
        <span class="muted" style="font-size:0.8em;">LLM model for avatar voice conversations.</span>
      </label>

      <label>
        <strong>Agent Workspace Folder</strong>
        <input
          type="text"
          .value=${form.workspaceDir ?? ""}
          @input=${(e: Event) => props.onConfigFormChange("workspaceDir", (e.target as HTMLInputElement).value)}
          style=${fieldStyle}
          placeholder="~/i-gloobot-files"
        />
        <span class="muted" style="font-size:0.8em;">Local folder where the avatar agent can read/write files</span>
      </label>

      <div style="display:flex;gap:8px;margin-top:8px;">
        <button class="btn btn--primary" @click=${props.onSaveConfig}>Save</button>
        <button class="btn" @click=${props.onCancelEdit}>Cancel</button>
      </div>
    </div>
  `;
}

function renderSessionsList(props: AvatarAdminProps) {
  const filtered = props.statusFilter
    ? props.sessions.filter((s) => s.status === props.statusFilter)
    : props.sessions;

  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <div class="card-title">
        Sessions
        <span class="muted">(${props.sessions.length})</span>
      </div>
      <div style="display:flex;gap:6px;">
        <select
          .value=${props.statusFilter}
          @change=${(e: Event) => props.onStatusFilterChange((e.target as HTMLSelectElement).value)}
          style="padding:4px 8px;border:1px solid var(--border);border-radius:4px;background:var(--bg-card);color:var(--fg);font-size:0.85em;"
        >
          <option value="">All</option>
          <option value="active">Active</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <button class="btn btn--sm" @click=${props.onRefresh} ?disabled=${props.loading}>
          Refresh
        </button>
      </div>
    </div>

    <div style="max-height:400px;overflow-y:auto;margin-top:8px;">
      ${
        filtered.length === 0
          ? html`
              <div class="muted" style="padding: 16px">No sessions found.</div>
            `
          : filtered.map(
              (s) => html`
              <div
                style="padding:8px 10px;border:1px solid var(--border);border-radius:6px;margin-bottom:6px;cursor:pointer;
                       ${props.selectedSession?.id === s.id ? "border-color:var(--accent);" : ""}"
                @click=${() => props.onSelectSession(s.id)}
              >
                <div style="display:flex;justify-content:space-between;align-items:center;">
                  <strong style="font-size:0.85em;">${s.livekitRoomId}</strong>
                  <span class="pill pill--sm ${s.status === "active" ? "pill--ok" : s.status === "failed" ? "" : ""}">
                    ${s.status}
                  </span>
                </div>
                <div class="muted" style="font-size:0.8em;margin-top:2px;">
                  ${s.userName ?? "Unknown user"}
                  ${s.projectName ? ` / ${s.projectName}` : ""}
                  ${s.durationSeconds != null ? ` / ${s.durationSeconds}s` : ""}
                  &middot; ${formatDate(s.createdAt)}
                </div>
              </div>
            `,
            )
      }
    </div>
  `;
}

function renderSessionDetail(props: AvatarAdminProps) {
  const s = props.selectedSession!;
  return html`
    <div style="display:flex;justify-content:space-between;align-items:center;">
      <div class="card-title">Session: ${s.livekitRoomId}</div>
      ${
        s.status === "active"
          ? html`
            <button
              class="btn btn--sm"
              style="color:var(--danger,#e55);"
              @click=${() => props.onEndSession(s.id)}
            >
              End Session
            </button>
          `
          : nothing
      }
    </div>

    <div class="detail-stat-grid" style="margin:12px 0;">
      <div class="stat">
        <div class="stat-value">
          <span class="pill pill--sm ${s.status === "active" ? "pill--ok" : ""}">
            ${s.status}
          </span>
        </div>
        <div class="stat-label">Status</div>
      </div>
      <div class="stat">
        <div class="stat-value">${s.durationSeconds != null ? `${s.durationSeconds}s` : "N/A"}</div>
        <div class="stat-label">Duration</div>
      </div>
      <div class="stat">
        <div class="stat-value">${s.userName ?? "N/A"}</div>
        <div class="stat-label">User</div>
      </div>
      <div class="stat">
        <div class="stat-value">${s.projectName ?? "N/A"}</div>
        <div class="stat-label">Project</div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:12px;">
      <div>
        <strong>Room URL:</strong>
        <div class="muted" style="margin-top:4px;word-break:break-all;">${s.livekitRoomUrl}</div>
      </div>
      <div>
        <strong>Avatar Image:</strong>
        <div class="muted" style="margin-top:4px;">${s.avatarImageUrl ?? "Default"}</div>
      </div>
    </div>

    ${
      s.metadata && Object.keys(s.metadata).length > 0
        ? html`
          <div style="margin-top:12px;">
            <strong>Metadata:</strong>
            <pre style="margin-top:4px;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.85em;">${JSON.stringify(s.metadata, null, 2)}</pre>
          </div>
        `
        : nothing
    }

    ${
      s.transcript
        ? html`
          <div style="margin-top:12px;">
            <strong>Transcript:</strong>
            <pre style="margin-top:4px;padding:8px;border:1px solid var(--border);border-radius:4px;background:var(--bg);color:var(--fg);font-size:0.85em;max-height:200px;overflow-y:auto;white-space:pre-wrap;">${s.transcript}</pre>
          </div>
        `
        : nothing
    }

    <div class="muted" style="font-size:0.8em;margin-top:12px;">
      Created: ${formatDate(s.createdAt)}
      ${s.completedAt ? ` / Completed: ${formatDate(s.completedAt)}` : ""}
    </div>
  `;
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "\u2014";
  try {
    const d = typeof value === "string" ? new Date(value) : value;
    return d.toLocaleString();
  } catch {
    return String(value);
  }
}
